/* ============================================================
   carrierPickupView — 63b51a7e: pure view model for the carrier
   ПВЗ map picker at cart checkout (Yandex Maps 3.0, clean no-key
   degrade). City selector clusters mirror the server-side
   DeliveryPointCityParser (42ca54ae: street-anchor parsing for full
   «Россия, Регион, Город, Улица…» addresses + provider-city
   preference) so the client filter and /cities endpoint agree.

   Pure functions; CarrierPickupMapPanel owns the state.
   Node-tested.
   ============================================================ */

import type { CatalogDeliveryPoint, CarrierPointSelection, DeliveryPointCity } from '../types/delivery.ts';

export type CarrierFilter = 'all' | 'ozon' | 'cdek' | 'russianpost' | 'pickup';

/** Word tokens that denote a street/square segment (server parity). */
const STREET_WORDS = new Set([
  'ул', 'улица', 'пр', 'просп', 'проспект', 'проезд', 'пер', 'переулок',
  'ш', 'шоссе', 'наб', 'набережная', 'бул', 'бульвар', 'пл', 'площадь',
  'тупик', 'аллея', 'линия', 'спуск', 'въезд', 'съезд', 'мкр', 'мкр.',
  'микрорайон', 'тер', 'территория', 'промзона', 'снт', 'гск',
]);

/** Word tokens that denote a house/building segment (server parity). */
const HOUSE_WORDS = new Set([
  'д', 'дом', 'влд', 'владение', 'стр', 'строение', 'к', 'корп', 'корпус',
  'лит', 'литера', 'дм', 'кв', 'офис', 'помещение',
]);

/** Leading markers stripped from a settlement segment («г. Пенза» → «Пенза»). */
const SETTLEMENT_MARKERS = [
  'город ', 'г. ', 'г ', 'пос. ', 'пос ', 'п. ', 'село ', 'с. ',
  'дер. ', 'деревня ', 'пгт ', 'рп. ', 'рп ', 'ст. ', 'станция ',
  'хутор ', 'аул ', 'поселок ', 'посёлок ',
];

/** Administrative (non-settlement) tokens (server parity). */
const ADMIN_WORDS = new Set([
  'россия', 'рф', 'область', 'обл', 'республика', 'респ', 'край',
  'округ', 'ао', 'район', 'р-н', 'муниципальный', 'федеральный',
]);

/** Bare republic/region names without «Республика»/«Область» suffixes. */
const BARE_REGIONS = new Set([
  'адыгея', 'алтай', 'башкортостан', 'бурятия', 'дагестан', 'ингушетия',
  'калмыкия', 'карачаево-черкесия', 'кабардино-балкария', 'карелия',
  'коми', 'крым', 'марий эл', 'мордовия', 'саха (якутия)', 'татарстан',
  'тыва', 'удмуртия', 'хакасия', 'чувашия', 'чукотка',
  'югра', 'кузбасс', 'днр', 'лнр', 'запорожье', 'херсон',
]);

function tokens(segment: string): string[] {
  return segment
    .split(' ')
    .map((t) => t.replace(/^\.+|\.+$/g, '').toLowerCase())
    .filter((t) => t.length > 0);
}

function startsOrEndsWithToken(toks: string[], words: ReadonlySet<string>): boolean {
  return words.has(toks[0]) || words.has(toks[toks.length - 1]);
}

function isStreetOrHouse(segment: string): boolean {
  if (segment.length > 0 && /\d/.test(segment[0])) return true; // bare house number
  const toks = tokens(segment);
  if (toks.length === 0) return false;
  return startsOrEndsWithToken(toks, STREET_WORDS) || startsOrEndsWithToken(toks, HOUSE_WORDS);
}

function isAdministrative(segment: string): boolean {
  if (BARE_REGIONS.has(segment.toLowerCase())) return true;
  const toks = tokens(segment);
  if (toks.length === 0) return false;
  for (const token of toks) {
    if (ADMIN_WORDS.has(token)) return true;
    for (const part of token.split('-')) {
      if (part && ADMIN_WORDS.has(part)) return true;
    }
  }
  const last = toks[toks.length - 1];
  if (last.length > 5 && /(ская|ский|ское)$/.test(last)) return true;
  return false;
}

function normalizeWhitespace(value: string | null | undefined): string | null {
  if (!value) return null;
  const collapsed = value.split(/\s+/).filter((s) => s.length > 0).join(' ');
  return collapsed.length > 0 ? collapsed : null;
}

function normalizeSettlement(segment: string): string | null {
  const name = normalizeWhitespace(segment);
  if (!name) return null;
  const lower = name.toLowerCase();
  for (const marker of SETTLEMENT_MARKERS) {
    if (lower.startsWith(marker)) {
      return name.slice(marker.length).trim();
    }
  }
  return name;
}

function looksLikeSettlement(name: string | null): name is string {
  if (!name || name.length < 2 || name.length > 64) return false;
  if (!/\p{L}/u.test(name)) return false; // pure digits/punctuation (bad feeds)
  if (isStreetOrHouse(name)) return false; // the «city» slot holds a street
  return true;
}

/**
 * Settlement from a one-line carrier address; null when unrecognized.
 * 42ca54ae: full «Россия, Регион, [Город], Улица…» shapes anchor on the
 * segment right before the first street/house segment (server parity).
 */
export function parseCityFromAddress(address: string | null | undefined): string | null {
  if (!address) return null;
  const segments = address.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  if (segments.length === 0) return null;

  // The settlement rides the segment right before the first street/house segment.
  for (let i = 1; i < segments.length; i++) {
    if (!isStreetOrHouse(segments[i])) continue;
    const candidate = normalizeSettlement(segments[i - 1]);
    if (candidate && looksLikeSettlement(candidate) && !isAdministrative(candidate)) return candidate;
    return null;
  }

  // No street segment (short shapes / partial feeds): first non-admin segment wins.
  for (const segment of segments) {
    if (isAdministrative(segment)) continue;
    const candidate = normalizeSettlement(segment);
    if (looksLikeSettlement(candidate)) return candidate;
    return null;
  }
  return null;
}

/**
 * 42ca54ae: canonical settlement for one point — the server-resolved
 * city (write-time provider city → parser) preferred, address parsing as
 * the fallback (server ResolveCity parity).
 */
export function resolvePointCity(point: Pick<CatalogDeliveryPoint, 'city' | 'address'>): string | null {
  const normalized = point.city?.trim();
  if (normalized && normalized.length >= 2) return normalized;
  return parseCityFromAddress(point.address);
}

/** Case-insensitive settlement comparison (selector ↔ parsed address). */
export function sameCity(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Points of one city + carrier (city null/blank = all cities). */
export function filterPoints(
  points: readonly CatalogDeliveryPoint[],
  city: string | null | undefined,
  carrier: CarrierFilter,
): CatalogDeliveryPoint[] {
  const wanted = city?.trim();
  return points.filter((p) => {
    if (carrier !== 'all' && p.carrier !== carrier) return false;
    if (!wanted) return true;
    return sameCity(resolvePointCity(p), wanted);
  });
}

/** Public carrier label for chips/markers. */
export function carrierLabel(carrier: string): string {
  switch (carrier) {
    case 'ozon': return 'Ozon';
    case 'cdek': return 'СДЭК';
    case 'russianpost': return 'Почта';
    case 'pickup': return 'Самовывоз';
    default: return carrier;
  }
}

/** Carrier chips with live counts (fixed order, only carriers present). */
export function carrierOptions(
  points: readonly CatalogDeliveryPoint[],
): { carrier: CarrierFilter; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const p of points) counts.set(p.carrier, (counts.get(p.carrier) ?? 0) + 1);
  const order: CarrierFilter[] = ['ozon', 'cdek', 'russianpost', 'pickup'];
  const options = order
    .filter((c) => counts.has(c))
    .map((c) => ({ carrier: c, label: carrierLabel(c), count: counts.get(c) ?? 0 }));
  return [{ carrier: 'all', label: 'Все', count: points.length }, ...options];
}

/**
 * City datalist: server clusters + fresh counts from the loaded page window.
 * cc177e39: an optional typed prefix filters the FULL server cluster list
 * (case-insensitive, startsWith) BEFORE the top-N cut — the plain top-50
 * by count hid every small settlement (Алушта, 19 pts) behind mega-cities,
 * resurrecting «далеко не все города» client-side. Empty prefix keeps the
 * historical top-N overview byte-identical.
 */
export function citySuggestions(
  serverCities: readonly DeliveryPointCity[],
  points: readonly CatalogDeliveryPoint[] | null,
  limit = 50,
  prefix = '',
): DeliveryPointCity[] {
  const counts = new Map<string, number>();
  for (const c of serverCities) counts.set(c.city, c.count);
  if (points) {
    const live = new Map<string, number>();
    for (const p of points) {
      const city = resolvePointCity(p);
      if (city) live.set(city, (live.get(city) ?? 0) + 1);
    }
    for (const [city, count] of live) counts.set(city, count);
  }
  const q = prefix.trim().toLowerCase();
  return [...counts.entries()]
    .filter(([city]) => !q || city.toLowerCase().startsWith(q))
    .map(([city, count]) => ({ city, count }))
    .sort((a, b) => b.count - a.count || a.city.localeCompare(b.city, 'ru'))
    .slice(0, limit);
}

/** Map center = centroid of the filtered window; Moscow default when empty. */
export function mapCenter(points: readonly CatalogDeliveryPoint[]): { lat: number; lon: number } {
  const withCoords = points.filter(
    (p) => p.geoLat !== null && p.geoLon !== null && Number.isFinite(Number(p.geoLat)) && Number.isFinite(Number(p.geoLon)),
  );
  if (withCoords.length === 0) return { lat: 55.7558, lon: 37.6173 };
  let lat = 0;
  let lon = 0;
  for (const p of withCoords) {
    lat += Number(p.geoLat);
    lon += Number(p.geoLon);
  }
  return { lat: lat / withCoords.length, lon: lon / withCoords.length };
}

/** Human list/marker label for one point. */
export function pointLabel(p: CatalogDeliveryPoint): string {
  const who = carrierLabel(p.carrier);
  const place = p.address ?? `ПВЗ ${p.externalNumber ?? p.externalId}`;
  return `${who} · ${place}`;
}

/** Selection payload for CheckoutDto (address snapshot rides the order). */
export function toSelection(p: CatalogDeliveryPoint): CarrierPointSelection {
  return {
    carrier: p.carrier as CarrierPointSelection['carrier'],
    externalId: p.externalId,
    address: p.address,
    geoLat: p.geoLat,
    geoLon: p.geoLon,
  };
}

/** Yandex Maps JS API 3.0 script URL; null when the key is absent (degrade to list).
 * 514: keys with «Подпись запросов» require the `signature` query param —
 * precomputed at build time (VITE_YMAPS_SIGN) over the fixed loader URL;
 * the signing secret itself never reaches the browser. `signature` must be
 * the last query parameter (Yandex Maps signature contract). */
export function ymapsScriptUrl(apiKey: string | undefined | null, signature?: string | null): string | null {
  const key = apiKey?.trim();
  if (!key) return null;
  const sig = signature?.trim();
  return `https://api-maps.yandex.ru/v3/?apikey=${encodeURIComponent(key)}&lang=ru_RU${sig ? `&signature=${encodeURIComponent(sig)}` : ''}`;
}

/**
 * 754df72d: operator hint for a degraded map. The script tag cannot read the
 * response body, so a dead/domain-restricted key (api-maps.yandex.ru answers
 * 403 JSON «Invalid api key», the browser blocks it as ORB) is reported by
 * the loader as a bare «script failed» — indistinguishable from a network
 * glitch. One short line tells ops which side to fix (key vs network).
 * Pure; node-tested.
 */
export function ymapsFailureHint(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (message.includes('script failed')) {
    return 'Карты не загрузились: ключ VITE_YMAPS_API_KEY отклонён (api-maps.yandex.ru отвечает 403 Invalid api key — браузер блокирует ответ) или нет сети до Яндекса.';
  }
  if (message.includes('ymaps3 missing')) {
    return 'Скрипт карт загрузился без API (window.ymaps3 отсутствует) — проверьте версию API в URL.';
  }
  return `Карты не загрузились: ${message || 'неизвестная ошибка'}.`;
}

/* ── 2026-09-03 (Артем): brand colors + count clustering ─────── */

/** Brand colors: Ozon blue, СДЭК green, Почта России dark blue, and the app
 * accent orange for the performer's own pickup points. Pure; node-tested. */
export function carrierColor(carrier: string): string {
  switch (carrier) {
    case 'ozon': return '#005BFF';
    case 'cdek': return '#009846';
    case 'russianpost': return '#103F87';
    case 'pickup': return '#FF9500';
    default: return '#6B7280';
  }
}

/** One map bubble: a single point or a merged group with its member count. */
export interface PointCluster {
  lat: number;
  lon: number;
  count: number;
  /** Carrier with the most members (bubble tint when uniform). */
  carrier: string;
  /** true when every member is one carrier → brand color, else neutral. */
  uniform: boolean;
  points: CatalogDeliveryPoint[];
}

/**
 * 2026-09-03 (Артем): grid clustering for the checkout map — nearby points
 * merge into one bubble that shows the member count («3» when three ПВЗ
 * collapse together). The cell halves per zoom level: z10 ≈ districts,
 * z12 ≈ neighborhoods, z14 ≈ street corners. Pure; node-tested.
 */
export function clusterPoints(
  points: readonly CatalogDeliveryPoint[],
  zoom: number,
): PointCluster[] {
  const withCoords = points.filter(
    (p) => p.geoLat !== null && p.geoLon !== null
      && Number.isFinite(Number(p.geoLat)) && Number.isFinite(Number(p.geoLon)),
  );
  const cell = 0.3 / Math.pow(2, Math.max(0, zoom - 10));
  const cells = new Map<string, CatalogDeliveryPoint[]>();
  for (const p of withCoords) {
    const lat = Number(p.geoLat);
    const lon = Number(p.geoLon);
    const key = `${Math.floor(lat / cell)}:${Math.floor(lon / cell)}`;
    const bucket = cells.get(key);
    if (bucket) bucket.push(p);
    else cells.set(key, [p]);
  }
  const clusters: PointCluster[] = [];
  for (const members of cells.values()) {
    let lat = 0;
    let lon = 0;
    const counts = new Map<string, number>();
    for (const p of members) {
      lat += Number(p.geoLat);
      lon += Number(p.geoLon);
      counts.set(p.carrier, (counts.get(p.carrier) ?? 0) + 1);
    }
    const [carrier] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]!;
    clusters.push({
      lat: lat / members.length,
      lon: lon / members.length,
      count: members.length,
      carrier,
      uniform: counts.size === 1,
      points: members,
    });
  }
  return clusters;
}
